/**
 * ViralAccountPage - Unified customer account dashboard for Viral Marketing System
 * Shows points, coupons, referral code, shares, and achievements
 * Fully responsive mobile-first design
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Star, Gift, Users, Camera, Trophy, Coffee,
  Copy, Share2, ChevronRight, Loader2, CheckCircle,
  Clock, Sparkles, TrendingUp, Crown, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import ReferralProgressDashboard from '@/components/account/ReferralProgressDashboard';
import { api } from '@/utils/apiClient';

// Tier configuration - dark mode compatible
const TIERS = {
  starter: { name: 'Coffee Starter', icon: Coffee, color: 'text-zinc-600 dark:text-zinc-400', bg: 'bg-zinc-100 dark:bg-zinc-800', min: 0 },
  regular: { name: 'Coffee Regular', icon: Coffee, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30', min: 500 },
  connoisseur: { name: 'Coffee Connoisseur', icon: Star, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30', min: 1000 },
  royalty: { name: 'Coffee Royalty', icon: Crown, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/30', min: 2500 }
};

// Points redemption options
const REDEMPTION_OPTIONS = [
  { points: 200, discount: 10 },
  { points: 400, discount: 15 },
  { points: 600, discount: 20 }
];

// Tab component
const Tab = ({ active, onClick, children, badge }) => (
  <button
    onClick={onClick}
    className={`
      relative px-4 py-3 text-sm font-medium transition-all
      ${active 
        ? 'text-primary border-b-2 border-primary' 
        : 'text-muted-foreground hover:text-foreground border-b-2 border-transparent'
      }
    `}
  >
    {children}
    {badge > 0 && (
      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

// Stat Card component
const StatCard = ({ icon: Icon, label, value, subtext, color = 'primary' }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      <span className="text-2xl font-bold">{value}</span>
    </div>
    <p className="text-sm font-medium mt-2">{label}</p>
    {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
  </div>
);

// Coupon Card component
const CouponCard = ({ coupon, onCopy }) => {
  const isExpiringSoon = coupon.expires_at && new Date(coupon.expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  
  return (
    <div className="bg-card border border-border rounded-xl p-4 relative overflow-hidden">
      {/* Decorative edge */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
      
      <div className="pl-3">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-2xl font-bold text-primary">{coupon.discount_percent}% OFF</span>
            {coupon.restrictions && (
              <p className="text-xs text-muted-foreground mt-1">{coupon.restrictions}</p>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onCopy(coupon.code)}
            className="text-xs"
          >
            <Copy className="w-3 h-3 mr-1" /> Copy
          </Button>
        </div>
        
        <div className="mt-3 flex items-center justify-between">
          <code className="bg-muted px-3 py-1 rounded text-sm font-mono">{coupon.code}</code>
          {isExpiringSoon && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Expiring soon
            </span>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground mt-2">{coupon.earned_reason}</p>
      </div>
    </div>
  );
};

// Achievement Badge component
const AchievementBadge = ({ achievement }) => {
  const isUnlocked = achievement.unlocked;
  
  return (
    <div className={`
      p-3 rounded-xl border text-center transition-all
      ${isUnlocked 
        ? 'bg-primary/10 border-primary/30' 
        : 'bg-muted/50 border-border opacity-50'
      }
    `}>
      <div className={`
        w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2
        ${isUnlocked ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
      `}>
        {isUnlocked ? (
          <Trophy className="w-6 h-6" />
        ) : (
          <span className="text-lg">?</span>
        )}
      </div>
      <p className="text-xs font-medium">{achievement.name}</p>
      <p className="text-[10px] text-muted-foreground">{achievement.description}</p>
      {!isUnlocked && (
        <div className="mt-2 w-full bg-muted rounded-full h-1">
          <div 
            className="bg-primary h-1 rounded-full transition-all"
            style={{ width: `${achievement.progress_percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default function ViralAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userIdFromUrl = searchParams.get('user_id');
  
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [coupons, setCoupons] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [coffeePassStatus, setCoffeePassStatus] = useState(null);
  
  // If no user_id, redirect to login
  useEffect(() => {
    if (!userIdFromUrl) {
      navigate('/my-account');
    }
  }, [userIdFromUrl, navigate]);
  
  // Load account data
  useEffect(() => {
    if (!userIdFromUrl) {
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        const [accountRes, couponsRes, achievementsRes, referralRes] = await Promise.allSettled([
          api.get(`/api/viral/account/${userIdFromUrl}`),
          api.get(`/api/viral/coupons/my?user_id=${userIdFromUrl}`),
          api.get(`/api/viral/account/${userIdFromUrl}/achievements`),
          api.get(`/api/viral/referral/stats?user_id=${userIdFromUrl}`)
        ]);
        
        if (accountRes.status === 'fulfilled') {
          setAccount(accountRes.value.data);
          
          // Fetch Coffee Pass status if we have user email or phone
          const userData = accountRes.value.data;
          if (userData?.email || userData?.phone) {
            try {
              const params = new URLSearchParams();
              if (userData.email) params.append('email', userData.email);
              if (userData.phone) params.append('phone', userData.phone);
              const passRes = await api.get(`/api/coffee-club/status?${params.toString()}`);
              setCoffeePassStatus(passRes.data);
            } catch (passErr) {
              console.log('No active coffee pass found');
            }
          }
        }
        
        if (couponsRes.status === 'fulfilled') {
          setCoupons(couponsRes.value.data.coupons || []);
        }
        
        if (achievementsRes.status === 'fulfilled') {
          setAchievements(achievementsRes.value.data.achievements || []);
        }
        
        if (referralRes.status === 'fulfilled') {
          setReferralStats(referralRes.value.data);
        }
      } catch (err) {
        console.error('Failed to load account:', err);
        toast.error('Failed to load account data');
      }
      setLoading(false);
    };
    
    loadData();
  }, [userIdFromUrl]);
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  // Share referral code
  const shareReferral = async () => {
    const shareData = {
      title: 'Join Bulaccino Cafe!',
      text: `Use my code ${referralStats?.referral_code} to get 10% off your first order at Bulaccino Cafe! Get the Bula Coffee Pass - FREE coffee every day for just $95/year!`,
      url: `https://bulaccinocafe.com/bula-coffee-club?ref=${referralStats?.referral_code}`
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyToClipboard(shareData.text + ' ' + shareData.url);
      }
    } else {
      copyToClipboard(shareData.text + ' ' + shareData.url);
    }
  };
  
  // Redeem points for coupon
  const redeemPoints = async (points) => {
    if (!account || account.points.available < points) {
      toast.error('Not enough points');
      return;
    }
    
    setRedeeming(true);
    try {
      const res = await api.post(`/api/viral/coupons/generate?user_id=${userIdFromUrl}`, { points_to_use: points });
      const data = res.data;
      
      if (data.success) {
        toast.success(`Coupon generated: ${data.coupon.code}`);
        // Refresh data
        setAccount(prev => ({
          ...prev,
          points: { ...prev.points, available: data.remaining_points }
        }));
        // Add new coupon to list
        setCoupons(prev => [
          { ...data.coupon, earned_reason: `Redeemed ${points} points` },
          ...prev
        ]);
      } else {
        toast.error(data.detail || 'Failed to generate coupon');
      }
    } catch (err) {
      toast.error('Failed to redeem points');
    }
    setRedeeming(false);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // No user ID
  if (!userIdFromUrl || !account) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <User className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-xl font-heading mb-2">Account Not Found</h1>
        <p className="text-muted-foreground mb-4">
          Please access this page via your account link or create an account first.
        </p>
        <Link to="/share">
          <Button>
            <Camera className="w-4 h-4 mr-2" />
            Share & Create Account
          </Button>
        </Link>
      </div>
    );
  }
  
  const tier = TIERS[account.tier] || TIERS.starter;
  const TierIcon = tier.icon;
  
  return (
    <>
      <Helmet>
        <title>My Rewards | Bulaccino Cafe</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-20">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-4 py-6 pb-16 relative z-0">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <User className="w-8 h-8" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-heading truncate">{account.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tier.bg} ${tier.color}`}>
                    <TierIcon className="w-3 h-3 inline mr-1" />
                    {tier.name}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Points Card - Overlapping */}
        <div className="max-w-lg mx-auto px-4 -mt-10">
          <div className="bg-card border border-border rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Available Points</p>
                <p className="text-4xl font-bold text-primary">{account.points.available}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Total earned: {account.points.total}
                </p>
              </div>
              <div className="text-right">
                <Link to="/share">
                  <Button size="sm" className="mb-2">
                    <Camera className="w-4 h-4 mr-1" /> Share & Earn
                  </Button>
                </Link>
                <p className="text-xs text-muted-foreground">200 pts = 10% off</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Coffee Pass Section - Show Stats if Active, or Promotion if Not */}
        <div className="max-w-lg mx-auto px-4 mt-6">
          {coffeePassStatus?.has_active_pass ? (
            /* Active Coffee Pass - Show Personal Stats */
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-5 text-white relative overflow-hidden shadow-lg ${
                coffeePassStatus.has_afternoon_window
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-500 dark:from-purple-600 dark:to-indigo-600'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 dark:from-emerald-600 dark:to-teal-600'
              }`}
              data-testid="coffee-pass-status-card"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <Coffee className="w-6 h-6" />
                  <span className="text-lg font-bold">Your Bula Coffee Pass</span>
                  {coffeePassStatus.has_afternoon_window && (
                    <span className="text-xs px-2 py-0.5 bg-white/20 rounded-full">
                      {coffeePassStatus.plan_type === 'extended' ? 'Extended' : 'Upgraded'}
                    </span>
                  )}
                  <CheckCircle className={`w-5 h-5 ${coffeePassStatus.has_afternoon_window ? 'text-purple-200' : 'text-emerald-200'}`} />
                </div>
                
                {/* Member Info - Show Redemption Windows Prominently for Extended */}
                {coffeePassStatus.has_afternoon_window ? (
                  <div className={`bg-white/20 rounded-xl p-3 mb-4`}>
                    <p className="text-purple-100 text-xs mb-1">Your Redemption Windows</p>
                    <p className="text-white font-bold text-lg">{coffeePassStatus.redemption_windows || '7:00 AM - 10:00 AM'}</p>
                    <p className="text-purple-200 text-xs mt-1">You have both morning AND afternoon access!</p>
                  </div>
                ) : (
                  <p className="text-emerald-100 text-sm mb-4">
                    {coffeePassStatus.plan_display_name || 'Active Member'} • {coffeePassStatus.redemption_windows || '7:00 AM - 10:00 AM'}
                  </p>
                )}
                
                {/* Redemption Counter - Key Feature */}
                <div className="bg-white/20 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${coffeePassStatus.has_afternoon_window ? 'text-purple-100' : 'text-emerald-100'}`}>Your Redemptions This Year</span>
                    <span className="text-2xl font-bold">{coffeePassStatus.total_redemptions || 0}</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(((coffeePassStatus.total_redemptions || 0) / 365) * 100, 100)}%` }}
                    />
                  </div>
                  <div className={`flex justify-between text-xs mt-1 ${coffeePassStatus.has_afternoon_window ? 'text-purple-100' : 'text-emerald-100'}`}>
                    <span>{coffeePassStatus.total_redemptions || 0} redeemed</span>
                    <span>365 possible</span>
                  </div>
                </div>
                
                {/* Pass Details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className={`text-xs ${coffeePassStatus.has_afternoon_window ? 'text-purple-100' : 'text-emerald-100'}`}>Pass Expires</p>
                    <p className="font-semibold">
                      {coffeePassStatus.expiry_date 
                        ? new Date(coffeePassStatus.expiry_date).toLocaleDateString('en-FJ', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })
                        : 'Active'}
                    </p>
                  </div>
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className={`text-xs ${coffeePassStatus.has_afternoon_window ? 'text-purple-100' : 'text-emerald-100'}`}>Today's Status</p>
                    <p className="font-semibold">
                      {coffeePassStatus.already_redeemed_today 
                        ? '✓ Redeemed Today' 
                        : coffeePassStatus.redemption_window_open 
                          ? '☕ Ready to Redeem!' 
                          : coffeePassStatus.has_afternoon_window
                            ? 'Next: Check windows above'
                            : 'Opens 7:00 AM'}
                    </p>
                  </div>
                </div>
                
                {/* Savings Highlight */}
                <div className="mt-4 pt-3 border-t border-white/20 text-center">
                  <p className={`text-sm ${coffeePassStatus.has_afternoon_window ? 'text-purple-100' : 'text-emerald-100'}`}>
                    You've saved approximately <span className="font-bold text-white">${((coffeePassStatus.total_redemptions || 0) * 6.50).toFixed(2)}</span> so far!
                  </p>
                  <p className={`text-xs mt-1 ${coffeePassStatus.has_afternoon_window ? 'text-purple-200' : 'text-emerald-200'}`}>(Based on avg $6.50 coffee price)</p>
                </div>
              </div>
            </motion.div>
          ) : (
            /* No Active Pass - Show Promotion Banner */
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600 rounded-2xl p-5 text-white relative overflow-hidden shadow-lg"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Coffee className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase tracking-wide">Founding Member Exclusive</span>
                </div>
                <h3 className="text-2xl font-bold mb-2">Bula Coffee Pass - $95</h3>
                <p className="text-sm opacity-95 mb-3">
                  365 days of FREE daily hot coffee (7am-10am & 1pm-3:30pm) • Hot coffee only
                </p>
                
                {/* Important Purchase Info */}
                <div className="bg-white/95 dark:bg-white/90 rounded-xl p-4 mb-3 text-gray-900">
                  <p className="text-sm font-bold text-amber-700 mb-2 flex items-center gap-2">
                    <span className="text-lg">📍</span> HOW TO PURCHASE:
                  </p>
                  <ul className="text-sm space-y-1.5 text-gray-800">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><strong>CASH ONLY</strong> at the cafe</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><strong>Location:</strong> Shop 2 & 3, Concave Dr, Namaka, Nadi</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><strong>Starting:</strong> Monday, June 15, 2026</span>
                    </li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <p className="text-xs text-red-600 font-semibold">
                      ⚠️ NOT available at Nadi Plaza or any other location
                    </p>
                  </div>
                </div>
                
                <div className="bg-white/20 rounded-lg p-3">
                  <p className="text-sm font-semibold">
                    Limited time offer!
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Referral Program Explanation */}
        <div className="max-w-lg mx-auto px-4 mt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-200 dark:border-green-800 rounded-2xl p-5"
          >
            <h3 className="text-lg font-bold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Earn FREE Discounts!
            </h3>
            <p className="text-sm text-green-700 dark:text-green-400 mb-4">
              Share your referral code with friends, family, WhatsApp contacts, and on social media. 
              When they sign up, YOU earn discounts to use at our grand opening!
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white dark:bg-green-900/30 rounded-lg p-2 text-center">
                <p className="font-bold text-green-700 dark:text-green-300">1 friend</p>
                <p className="text-green-600 dark:text-green-400">10% off</p>
              </div>
              <div className="bg-white dark:bg-green-900/30 rounded-lg p-2 text-center">
                <p className="font-bold text-green-700 dark:text-green-300">3 friends</p>
                <p className="text-green-600 dark:text-green-400">15% off</p>
              </div>
              <div className="bg-white dark:bg-green-900/30 rounded-lg p-2 text-center">
                <p className="font-bold text-green-700 dark:text-green-300">5 friends</p>
                <p className="text-green-600 dark:text-green-400">20% off</p>
              </div>
              <div className="bg-white dark:bg-green-900/30 rounded-lg p-2 text-center">
                <p className="font-bold text-green-700 dark:text-green-300">10+ friends</p>
                <p className="text-green-600 dark:text-green-400">25% off</p>
              </div>
            </div>
            <p className="text-xs text-green-600 dark:text-green-500 mt-3 text-center">
              Discounts are 7-day coupons. Use them at our grand opening!
            </p>
          </motion.div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-lg mx-auto px-4 mt-6">
          <div className="flex border-b border-border overflow-x-auto">
            <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              Overview
            </Tab>
            <Tab active={activeTab === 'coupons'} onClick={() => setActiveTab('coupons')} badge={coupons.length}>
              Coupons
            </Tab>
            <Tab active={activeTab === 'referrals'} onClick={() => setActiveTab('referrals')}>
              Referrals
            </Tab>
            <Tab active={activeTab === 'achievements'} onClick={() => setActiveTab('achievements')}>
              Achievements
            </Tab>
          </div>
        </div>
        
        {/* Tab Content */}
        <div className="max-w-lg mx-auto px-4 mt-6">
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    icon={Camera}
                    label="Total Shares"
                    value={account.stats.total_shares}
                  />
                  <StatCard
                    icon={Users}
                    label="Referrals"
                    value={account.stats.successful_referrals}
                  />
                </div>
                
                {/* Redeem Points */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="font-heading mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    Redeem Points
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {REDEMPTION_OPTIONS.map(option => (
                      <button
                        key={option.points}
                        onClick={() => redeemPoints(option.points)}
                        disabled={account.points.available < option.points || redeeming}
                        className={`
                          p-3 rounded-lg border text-center transition-all
                          ${account.points.available >= option.points 
                            ? 'border-primary/50 hover:bg-primary/10 cursor-pointer' 
                            : 'border-border opacity-50 cursor-not-allowed'
                          }
                        `}
                      >
                        <p className="text-lg font-bold text-primary">{option.discount}%</p>
                        <p className="text-xs text-muted-foreground">{option.points} pts</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Referral Code */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="font-heading mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    Your Referral Code
                  </h3>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-muted px-4 py-3 rounded-lg text-lg font-mono text-center">
                      {referralStats?.referral_code || '...'}
                    </code>
                    <Button 
                      variant="outline"
                      onClick={() => copyToClipboard(referralStats?.referral_code)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button onClick={shareReferral}>
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share your code and earn 10% off when friends make a purchase!
                  </p>
                </div>
                
                {/* Recent Activity */}
                {account.recent_activity?.points?.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-4">
                    <h3 className="font-heading mb-3">Recent Activity</h3>
                    <div className="space-y-2">
                      {account.recent_activity.points.slice(0, 5).map((activity, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <div>
                            <p className="text-sm">{activity.reason.replace('_', ' ')}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(activity.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`font-bold ${activity.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {activity.points > 0 ? '+' : ''}{activity.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
            
            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <motion.div
                key="coupons"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {coupons.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-heading mb-2">No Active Coupons</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Earn points by sharing and redeem them for discounts!
                    </p>
                    <Link to="/share">
                      <Button>
                        <Camera className="w-4 h-4 mr-2" /> Share to Earn Points
                      </Button>
                    </Link>
                  </div>
                ) : (
                  coupons.map((coupon, i) => (
                    <CouponCard 
                      key={coupon.code || i} 
                      coupon={coupon} 
                      onCopy={copyToClipboard}
                    />
                  ))
                )}
              </motion.div>
            )}
            
            {/* Referrals Tab */}
            {activeTab === 'referrals' && (
              <motion.div
                key="referrals"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Referral Stats */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-primary">
                        {referralStats?.successful_purchases || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Friends Who Purchased</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold">
                        {referralStats?.total_signups || 0}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Signups</p>
                    </div>
                  </div>
                </div>
                
                {/* Share Code */}
                <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
                  <h3 className="font-heading mb-2">Share Your Code</h3>
                  <code className="block bg-white/50 dark:bg-black/20 px-4 py-3 rounded-lg text-xl font-mono text-center mb-3">
                    {referralStats?.referral_code}
                  </code>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => copyToClipboard(referralStats?.referral_code)}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copy Code
                    </Button>
                    <Button className="flex-1" onClick={shareReferral}>
                      <Share2 className="w-4 h-4 mr-1" /> Share
                    </Button>
                  </div>
                </div>
                
                {/* Tier Progress */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="font-heading mb-3">Referral Rewards</h3>
                  <div className="space-y-3">
                    {[1, 3, 5, 10, 25].map(tier => {
                      const isUnlocked = (referralStats?.successful_purchases || 0) >= tier;
                      const reward = tier === 1 ? '10% off pastry/pie' : 
                                     tier === 3 ? '15% off visit' :
                                     tier === 5 ? '20% off visit' :
                                     tier === 10 ? '25% off visit (max)' :
                                     '25% off + Ambassador';
                      return (
                        <div 
                          key={tier}
                          className={`flex items-center gap-3 p-2 rounded-lg ${isUnlocked ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isUnlocked ? 'bg-green-500 text-white' : 'bg-muted'}`}>
                            {isUnlocked ? <CheckCircle className="w-4 h-4" /> : tier}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{tier} friend{tier > 1 ? 's' : ''}</p>
                            <p className="text-xs text-muted-foreground">{reward}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <motion.div
                key="achievements"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    {achievements.filter(a => a.unlocked).length} / {achievements.length} unlocked
                  </p>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  {achievements.map((achievement, i) => (
                    <AchievementBadge key={achievement.id || i} achievement={achievement} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
